import { Box, VStack } from 'native-base';
import { useSpringBigStore, pluralizeWithWord, useI18n, Reward } from '@springbig/shared';
import React, { useEffect, useRef, useState } from 'react';
import { Caption, H1, H3, H4 } from './Typography';
import CompactImagesGallery from './CompactImagesGallery';
import SimpleProgressBar from '~/shared/SimpleProgressBar';
import { SpIcon } from '~/shared/Icon';
import { faStar } from '@fortawesome/free-solid-svg-icons';
import { useTheme } from '@springbig/shared';
import { Button, Modal } from '~/shared/Theme';

export type ModalProps = {
    showModal: boolean;
    extensionId: number;
    type: 'rewards' | 'offers';
    onCloseModal: () => void;
    onRedeem: () => void;
    isInStore?: boolean;
};

const ExtensionDetailsModal = ({
    showModal,
    type,
    extensionId,
    onCloseModal,
    onRedeem,
    isInStore
}: ModalProps) => {

    const {
        primary,
        highlight_text,
    } = useTheme();

    const { t } = useI18n();

    const { member } = useSpringBigStore();

    const modalRef = useRef<HTMLDivElement>(null);

    const [modalWidth, setModalWidth] = useState(null);
    const [reward, setReward] = useState<Reward | undefined>(null);
    const [images, setImages] = useState(null);

    useEffect(() => {

        if (!member || !member.rewards) {
            return;
        }

        if (type === 'rewards') {
            setReward(member.rewards.find(targetReward => targetReward.id === extensionId));
        }
        else {
            setReward(member.reward_grants.find(targetReward => targetReward.id === extensionId)?.reward);
        }
    }, [extensionId]);

    useEffect(() => {

        if (!reward) {
            return;
        }

        setImages(reward?.images.filter(image => image.label === 'preload'));
    }, [reward]);

    useEffect(() => {

        if (modalRef.current) {
            setModalWidth((modalRef.current).offsetWidth);
        }
    });

    useEffect(() => {

        const handleResize = () => {

            if (modalRef.current) {
                setModalWidth((modalRef.current).offsetWidth);
            }
        };

        window.addEventListener('resize', handleResize);
        handleResize();

        return () => {

            window.removeEventListener('resize', handleResize);
        };
    }, [modalRef.current]);

    const handleRedeem = (): void => {

        onRedeem();
    };

    const handleCloseModal = (): void => {

        onCloseModal();
    };

    const handleLinkPress = (url: string): void => {

        if (!url) {
            return;
        }

        window.open(url, '_blank');
    };

    if (reward?.external_source_id) {
        return (
            reward && (
                <Modal isOpen={showModal} ref={modalRef}>
                    <Modal.Header>
                        <H1 textAlign={'center'} color={highlight_text}>
                            {t('rewards.external_source.title')}
                        </H1>
                    </Modal.Header>
                    <Modal.Body
                        textAlign={'center'}
                        h={'100%'}
                    >
                        <Caption textAlign={'center'}>
                            {t('rewards.external_source.where_redeem')}
                        </Caption>
                        <Caption py={4} textAlign={'center'}>
                            {t('rewards.external_source.if_redeeming_in_store')}
                        </Caption>
                    </Modal.Body>
                    <Modal.Footer
                        justifyContent={'center'}
                    >
                        <VStack
                            alignItems={'center'}
                            textAlign={'center'}
                            maxW={'100%'}
                        >
                            <Button
                                text={t('rewards.external_source_redeemption.redeem_offer')}
                                variant={'button'}
                                onPress={() => handleRedeem()}
                            />
                            <Button
                                text={t('rewards.external_source_redeemption.external_cta')}
                                textValues={[`${type}.modal_title`]}
                                variant={'text'}
                                onPress={() => handleCloseModal()}
                            />
                        </VStack>
                    </Modal.Footer>
                </Modal>
            )
        );
    }

    return (
        reward && (
            <Modal isOpen={showModal} ref={modalRef}>
                <Modal.Header>
                    { images && images.length ? (
                        <CompactImagesGallery imagesProp={images} width={modalWidth} />
                    ) : (
                        <Box
                            w={modalWidth / 2}
                            h={modalWidth / 2}
                            mx={'auto'}
                            alignItems={'center'}
                            justifyContent={'center'}
                        >
                            <SpIcon
                                icon={faStar}
                                size={modalWidth / 2}
                                color={primary}
                            />
                        </Box>
                    )}
                </Modal.Header>
                <Modal.Body
                    textAlign={'center'}
                    h={'100%'}
                >
                    <VStack space={4}>
                        <Box>
                            <H3 textAlign={'center'}>
                                { reward.name }
                            </H3>
                            { type === 'rewards' &&
                                    <Caption opacity={.75} textAlign={'center'}>
                                        { reward.item_value } { pluralizeWithWord({ quantity: reward.item_value, singularWord: t('redeem.point'), pluralWord: t('redeem.points') }) }
                                    </Caption>
                            }
                            { type === 'offers' && reward.expiration_date &&
                                    <Caption opacity={.75} textAlign={'center'}>
                                        expires { reward.expiration_date }
                                    </Caption>
                            }
                        </Box>
                        { reward?.description &&
                                <Caption
                                    w={'100%'}
                                    textAlign={'center'}
                                    opacity={0.75}
                                >
                                    { reward?.description }
                                </Caption>
                        }
                        { reward?.product_url_attributes?.value &&
                                <Button variant={'text'} onPress={() => handleLinkPress(reward.product_url_attributes.value)}>
                                    <H4 color={highlight_text}>
                                        { reward.product_url_attributes.name }
                                    </H4>
                                </Button>
                        }
                        { reward?.offer_effects_attributes?.length > 0 &&
                                <Box>
                                    { reward.offer_effects_attributes.map(effect =>

                                        <SimpleProgressBar key={effect.id} progress={effect.value} title={effect.name} />
                                    )}
                                </Box>
                        }
                        { !isInStore &&
                                <Caption text={`${type}.redemption_text`} textValues={[reward.name]} />
                        }
                    </VStack>
                </Modal.Body>
                <Modal.Footer
                    justifyContent={'center'}
                >
                    <VStack
                        alignItems={'center'}
                        textAlign={'center'}
                        maxW={'100%'}
                    >
                        { !isInStore ? (member.balance >= reward.item_value ? (
                            <Button
                                text={'redeem.with_staff'}
                                textValues={[type.slice(0, -1)]}
                                onPress={() => handleRedeem()}
                            />
                        ) : (
                            <Caption>
                                <Caption>you need </Caption>
                                <Caption fontWeight='bold'>
                                    { reward.item_value } { pluralizeWithWord({ quantity: reward.item_value, singularWord: t('redeem.point'), pluralWord: t('redeem.points') }) }
                                </Caption>
                                <Caption> to redeem { reward.name }</Caption>
                            </Caption>
                        )) :
                            <VStack space={4}>
                                <H3 text={`${type}.modal_title`} />
                                <Caption
                                    fontSize={12}
                                    text={`${type}.redemption_in_store`}
                                />
                            </VStack>
                        }
                        <Button
                            text={'rewards.return_to'}
                            textValues={[type]}
                            variant={'text'}
                            onPress={() => handleCloseModal()}
                        />
                    </VStack>
                </Modal.Footer>
            </Modal>
        )
    );
};

export default ExtensionDetailsModal;
